import React from 'react';
import { Move, RotateCw } from 'lucide-react';

interface SelectionOverlayProps {
  bounds: {
    left: number;
    top: number;
    width: number;
    height: number;
    rotation?: number;
  };
  isDragging?: boolean;
  isLocked?: boolean;
  onResizeStart?: (handle: string, e: React.MouseEvent) => void;
  onRotateStart?: (e: React.MouseEvent) => void;
  onDragStart?: (e: React.MouseEvent) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  lastGlobalClickRef?: React.MutableRefObject<{ time: number, id: string | null } | null>;
}

export const SelectionOverlay: React.FC<SelectionOverlayProps> = ({
  bounds,
  isDragging,
  isLocked,
  onResizeStart,
  onRotateStart,
  onDragStart,
  onDoubleClick,
  lastGlobalClickRef
}) => {
  // Refined, Professional Tool Specs
  const handleSize = 24;
  const offset = -12; // Half of handleSize (24/2) to center it on the stroke
  const strokeWidth = 6;
  const primaryColor = '#30A84B'; // Gastronomy Green

  const handleMouseDown = (handle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLocked) return;
    if (onResizeStart) onResizeStart(handle, e);
  };

  const handleRotateMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLocked) return;
    if (onRotateStart) onRotateStart(e);
  };

  const handleMoveMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLocked) return;

    const now = Date.now();
    const lastClick = lastGlobalClickRef?.current || { time: 0, id: null };
    const timeSinceLastClick = now - lastClick.time;

    const isManualDoubleClick = timeSinceLastClick > 20 && timeSinceLastClick < 500;

    if ((e.detail >= 2 || isManualDoubleClick) && onDoubleClick) {
      onDoubleClick(e);
      if (lastGlobalClickRef) lastGlobalClickRef.current = { time: 0, id: null };
      return;
    }

    if (lastGlobalClickRef) lastGlobalClickRef.current = { time: now, id: 'overlay' };

    if (onDragStart) onDragStart(e);
  };

  return (
    <div
      className="absolute pointer-events-none select-none"
      style={{
        left: `${bounds.left}px`,
        top: `${bounds.top}px`,
        width: `${bounds.width}px`,
        height: `${bounds.height}px`,
        zIndex: 10000,
        transform: bounds.rotation ? `rotate(${bounds.rotation}deg)` : undefined,
      }}
    >
      {/* Selection outline - Crisp & Professional */}
      {!isDragging && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            border: `${strokeWidth}px solid ${primaryColor}`,
            boxShadow: `0 0 0 1.5px white, 0 0 20px rgba(48, 168, 75, 0.5), 0 8px 32px rgba(0, 0, 0, 0.3)`,
            borderRadius: '2px',
          }}
        />
      )}

      {/* Dimension Tooltip - Positioned at the TOP to stay clear of Controls */}
      {(bounds.width > 0 || bounds.height > 0) && !isLocked && (
        <div
          className={`absolute left-1/2 -translate-x-1/2 flex items-center gap-3 bg-gray-950 text-white text-[13px] font-black px-4 py-2 rounded-full shadow-[0_12px_32px_rgba(0,0,0,0.5)] border border-white/20 whitespace-nowrap z-[10001] pointer-events-none transition-all duration-300 ${bounds.top < 100 ? 'top-[calc(100%+80px)]' : '-top-16'
            }`}
        >
          <div className="flex items-center gap-1.5">
            <span className="text-[#30A84B]">W</span>
            <span className="tabular-nums">{Math.round(bounds.width)}</span>
          </div>
          <div className="w-[1px] h-4 bg-white/20" />
          <div className="flex items-center gap-1.5">
            <span className="text-[#30A84B]">H</span>
            <span className="tabular-nums">{Math.round(bounds.height)}</span>
          </div>
          {bounds.rotation ? (
            <>
              <div className="w-[1px] h-4 bg-white/20" />
              <div className="flex items-center gap-2">
                <RotateCw className="w-3.5 h-3.5 text-blue-400" />
                <span className="tabular-nums">{Math.round(((bounds.rotation % 360) + 360) % 360)}°</span>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* Control Bar - Positioned at the BOTTOM - Enlarged for better usability */}
      {!isDragging && !isLocked && (
        <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-gray-950 p-2.5 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.7)] border border-white/10 pointer-events-auto">
          <button
            className="w-14 h-14 flex items-center justify-center rounded-xl text-white hover:bg-[#30A84B] active:scale-95 transition-all cursor-move group relative"
            onMouseDown={handleMoveMouseDown}
            title="Move Layer"
          >
            <Move className="w-7 h-7 group-hover:scale-110 transition-transform" />
            <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#30A84B] rounded-full animate-pulse" />
          </button>
          <div className="w-[1px] h-8 bg-white/10 mx-1" />
          <button
            className="w-14 h-14 flex items-center justify-center rounded-xl text-white hover:bg-blue-600 active:scale-95 transition-all cursor-alias group"
            onMouseDown={handleRotateMouseDown}
            title="Rotate Layer"
          >
            <RotateCw className="w-7 h-7 group-hover:rotate-90 transition-transform duration-300" />
          </button>
        </div>
      )}

      {/* Handles - Crisp Figma-Style Handles */}
      {!isDragging && !isLocked && (
        <>
          {/* Corner Handles */}
          {[
            { id: 'top-left', style: { left: offset, top: offset, cursor: 'nwse-resize' } },
            { id: 'top-right', style: { right: offset, top: offset, cursor: 'nesw-resize' } },
            { id: 'bottom-left', style: { left: offset, bottom: offset, cursor: 'nesw-resize' } },
            { id: 'bottom-right', style: { right: offset, bottom: offset, cursor: 'nwse-resize' } },
          ].map(h => (
            <div
              key={h.id}
              className="absolute bg-white border-[2px] border-[#30A84B] rounded-md pointer-events-auto shadow-sm hover:scale-110 hover:shadow-md active:bg-[#30A84B] transition-all duration-150 z-[10002]"
              onMouseDown={(e) => handleMouseDown(h.id, e)}
              onClick={(e) => e.stopPropagation()}
              style={{
                ...h.style,
                width: `${handleSize}px`,
                height: `${handleSize}px`,
              }}
            />
          ))}

          {/* Edge Handles - Robust Pills */}
          {[
            { id: 'top', style: { left: '50%', top: -8, transform: 'translateX(-50%)', cursor: 'ns-resize', width: 40, height: 16 } },
            { id: 'bottom', style: { left: '50%', bottom: -8, transform: 'translateX(-50%)', cursor: 'ns-resize', width: 40, height: 16 } },
            { id: 'left', style: { left: -8, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize', width: 16, height: 40 } },
            { id: 'right', style: { right: -8, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize', width: 16, height: 40 } },
          ].map(h => (
            <div
              key={h.id}
              className="absolute bg-white border-[1.5px] border-[#30A84B] rounded-full pointer-events-auto shadow-sm hover:scale-105 active:bg-[#30A84B] transition-all duration-150 z-[10002]"
              onMouseDown={(e) => handleMouseDown(h.id, e)}
              onClick={(e) => e.stopPropagation()}
              style={{
                ...h.style,
              }}
            />
          ))}
        </>
      )}
    </div>
  );
};
